import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Accordion, AccordionDetails, AccordionSummary, Button } from '@mui/material'
import { SynerallContactV2 } from '@vae/server/src/types/synerallApiTypes'
import React, { useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import StringInput from '../../../components/inputs/StringInput'
import useUser from '../../../contexts/hooks/useUser'
import { ObjectContext } from '../../../contexts/objectContext/ObjectContext'
import { useSyneContractInfo } from '../hooks/trpcHooks'

export interface ContactFieldProps {
	contact: SynerallContactV2
	label: string
	isLoading: boolean
	editMode: { [key: string]: boolean }
	contactValues: { [key: number]: string }
	onEdit: (id: number, value: string) => void
	onChange: (id: number, value: string) => void
	onSave: (contact: SynerallContactV2) => void
}

export const ContactField = ({
	contact,
	label,
	isLoading,
	editMode,
	contactValues,
	onEdit,
	onChange,
	onSave,
}: ContactFieldProps) => {
	const { t } = useTranslation('pages_contractInformation')

	return (
		<div className='contact-field'>
			<div className='edit-container'>
				<div className='w-full'>
					<StringInput
						id={`contact-input-${contact.Id}`}
						label={label}
						value={contactValues[contact.Id] || contact.Value}
						onChange={value => onChange(contact.Id, value)}
						readonly={!editMode[contact.Id]}
						disabled={isLoading || !editMode[contact.Id]}
					/>
				</div>
				{editMode[contact.Id] ? (
					<Button onClick={() => onSave(contact)} className='!w-fit m-0' disabled={isLoading}>
						{t('save')}
					</Button>
				) : (
					<Button
						onClick={() => onEdit(contact.Id, contact.Value)}
						className='!w-fit m-0'
						disabled={isLoading}
					>
						{t('edit')}
					</Button>
				)}
			</div>
		</div>
	)
}

const ContactInfoAccordion = () => {
	const { t } = useTranslation('pages_contractInformation')

	const [editMode, setEditMode] = useState<{ [key: string]: boolean }>({})
	const [contactValues, setContactValues] = useState<{ [key: number]: string }>({})

	const object = useContext(ObjectContext)
	const { user } = useUser()

	const { syneUser, isLoading, updateContact } = useSyneContractInfo(
		user?.personalId,
		object.synerallId,
		object.synerallContractId
	)

	const handleEditToggle = (contactId: number, currentValue: string) => {
		setEditMode(prev => ({ ...prev, [contactId]: !prev[contactId] }))
		setContactValues(prev => ({ ...prev, [contactId]: currentValue }))
		// setTimeout needed to wait for readonly to be removed before focusing
		setTimeout(() => {
			const inputElement = document.getElementById(`contact-input-${contactId}`) as HTMLInputElement
			if (inputElement) inputElement.focus()
		}, 0)
	}

	const handleContactChange = (contactId: number, value: string) => {
		setContactValues(prev => ({ ...prev, [contactId]: value }))
	}

	const handleContactSave = (contact: SynerallContactV2) => {
		const newValue = contactValues[contact.Id]
		if (newValue && newValue !== contact.Value) {
			if (!syneUser?.Id || !object.synerallId) return
			updateContact({
				synerallId: object.synerallId,
				customerID: syneUser.Id,
				contactId: contact.Id,
				Primary: contact.Primary,
				Type: contact.Type,
				Value: newValue,
			})
		}
		setEditMode(prev => ({ ...prev, [contact.Id]: false }))
	}

	const contactGroups = useMemo(
		() => [
			{
				label: t('billing.email'),
				// TODO: fix Contact.Type upstream to use the union type instead of string
				contacts: (syneUser?.Contacts.filter(c => c.Type === 'Email') ?? []) as SynerallContactV2[],
			},
			{
				label: t('billing.phone'),
				// TODO: fix Contact.Type upstream to use the union type instead of string
				contacts: (syneUser?.Contacts.filter(c => c.Type === 'Gsm') ?? []) as SynerallContactV2[],
			},
		],
		[syneUser, t]
	)

	return (
		<Accordion className='contact-information'>
			<AccordionSummary
				expandIcon={<ExpandMoreIcon />}
				aria-controls='contact-content'
				id='contact-header'
			>
				<div className='title'>{t('billing.contactInformation')}</div>
			</AccordionSummary>
			<AccordionDetails>
				<div className='contact-information'>
					{contactGroups.flatMap(({ label, contacts }) =>
						contacts.map(contact => (
							<ContactField
								key={contact.Id}
								contact={contact}
								label={label}
								isLoading={isLoading}
								editMode={editMode}
								contactValues={contactValues}
								onEdit={handleEditToggle}
								onChange={handleContactChange}
								onSave={handleContactSave}
							/>
						))
					)}
				</div>
			</AccordionDetails>
		</Accordion>
	)
}

export default ContactInfoAccordion
